import Vue from 'vue';
import api from '../../api/event';
import Utils from './base.js';
import CONFIG from '@/constants/config.js';
import { DEFAULT_SORT_ORDER } from '@/utils/event-list.mjs';
import { normalizeCustomKeywordDefinitions, replaceCustomKeywords } from '@/utils/custom-keywords.mjs';

// event fields whose [custom:...] usage tokens are substituted once at load time
// (same list as the app's EventManager: description, custom contents, finish text)
const CUSTOM_KEYWORD_EVENT_FIELDS = [
    'evcommentdt',
    'custom_content1',
    'custom_content2',
    'custom_content3',
    'finish_text',
];

// footer layout used when the event delivers no (or an unusable) custom_app_layout
const DEFAULT_APP_LAYOUT = '{"first":"map","second":"info","third":"highscore","fourth":"tasks"}';

// env flags arrive as strings
const flagIsTrue = (value) => value === true || ['true', '1'].includes(String(value).trim().toLowerCase());
const INITIAL_SORT_OPTION =
    flagIsTrue(CONFIG.SORT_EVENT_LIST_BY_DISTANCE) && !flagIsTrue(CONFIG.DISABLE_EVENT_SORT_BY_DISTANCE)
        ? 'byDistance'
        : 'byLastUsed';

const methods = {
    mapEvent(event) {
        return event && event.data ? event.data : event;
    },
    ingestCustomKeywords(event) {
        const definitions = normalizeCustomKeywordDefinitions(event.custom_keywords);
        CUSTOM_KEYWORD_EVENT_FIELDS.forEach((field) => {
            if (typeof event[field] === 'string') {
                event[field] = replaceCustomKeywords(event[field], definitions);
            }
        });
        return definitions;
    },
};

const state = {
    gametype: CONFIG.GAMETYPE_LBG,
    app_identifier: null,
    events: [],
    selected_event: {},
    selected_event_id: null,
    event_timezone: null,
    app_user_statistic: null,
    app_team_photo: null,
    app_team_name: null,
    app_team: null,
    event_loading: false,
    event_single_loading: false,
    event_single_native_loading: false,
    event_error: false,
    entered_passwords: [],
    // [custom:...] definitions of the selected event, delivered by the server on event checkin
    custom_keywords: {},

    event_sort_order: DEFAULT_SORT_ORDER[INITIAL_SORT_OPTION],
    event_sort_option: INITIAL_SORT_OPTION,
};

// getters
const getters = {
    /**
     * @returns the custom app footer layout of the selected event as a JSON string, or null
     * while no event is selected (keeps the footer empty until the checkin has finished).
     * Falls back to DEFAULT_APP_LAYOUT when the event delivers no layout, an empty one or
     * invalid JSON, so a non-null result can always be parsed.
     */
    getSelectedEventFooterLayout:(state)=>{
        if (!state.selected_event || !state.selected_event.id_event) {
            return null;
        }
        const layout = state.selected_event.custom_app_layout;
        if (typeof layout === 'string' && layout.trim() !== '') {
            try {
                if (Object.values(JSON.parse(layout)).length > 0) {
                    return layout;
                }
            } catch (ex) {
                // unusable layout, fall through to the default
            }
        }
        return DEFAULT_APP_LAYOUT;
    },
    // disable_map_task_list of the checkin response; a missing field keeps the task list drawer
    mapTaskListDisabled: (state) => !!(state.selected_event && state.selected_event.disable_map_task_list),
    /**
     * Custom page title
     * @param {*} state
     * @returns string title
     */
    getCustomPageTitle:(state)=>(id)=>{
        const dynamicProperty = `custom_title${id}`;
        return  state.selected_event && state.selected_event[dynamicProperty] ? state.selected_event[dynamicProperty] : '';
    },
    /**
     * Custom page content
     * @param {*} state
     * @returns string html content
     */
    getCustomPageContent:(state)=>(id)=>{
        const dynamicProperty = `custom_content${id}`;
        return state.selected_event && state.selected_event[dynamicProperty] ? state.selected_event[dynamicProperty] : '';
    },
    firstEvent: (state) => {
        return state.events && state.events.length > 0 ? Vue._.first(state.events) : null;
    },
    firstXEvents: (state) => (count) => {
        return Vue._.slice(state.events, 0, count);
    },
    allEvents: (state) => {
        return state.events;
    },
    hasSelectedEvent: (state) => {
        return !Vue._.isEmpty(state.selected_event);
    },
    selectedEvent: (state) => {
        return state.selected_event;
    },
    selectedEventId: (state) => {
        return state.selected_event_id;
    },
    selectedEventTimezone: (state) => {
        return state.event_timezone;
    },
    enteredPasswords: (state) => {
        return state.entered_passwords;
    },
    team: (state) => {
        return state.app_team;
    },
    teamId: (state) => {
        return state.app_team && state.app_team.team ? state.app_team.team.tid : null;
    },
    teamPersonId: (state) => {
        return state.app_team && state.app_team.team ? state.app_team.team.pid : null;
    },
    teamMembers: (state) => {
        return state.app_team && state.app_team.members ? state.app_team.members : null;
    },
    teamMemberById: (state, getters) => (id) => {
        return getters.teamMembers ? getters.teamMembers.find((user) => user.id == id) : null;
    },
    teamLeader: (state) => {
        return state.app_team && state.app_team.teamleader ? state.app_team.teamleader : null;
    },
};

// actions
const actions = {
    // options: array of gametype ids, or {gametypes, force} where force bypasses the GET cache
    getEvents({ commit }, options) {
        commit('SET_EVENT_LOADING', true);
        return new Promise((resolve, reject) => {
            let promises = [];
            let gametypes = Vue._.isArray(options) ? options : options && options.gametypes;
            let force = !Vue._.isArray(options) && !!(options && options.force);
            gametypes = Vue._.isArray(gametypes) ? gametypes : [CONFIG.GAMETYPE_LBG];
            gametypes.forEach((gametype_id) => {
                promises.push(
                    api.getEvents(
                        {
                            gametype_id: gametype_id,
                        },
                        force,
                    ),
                );
            });

            Promise.all(promises)
                .then((responseArray) => {
                    let events = [];
                    // the list derives the server clock from current_time plus the time since the response
                    let receivedAt = Date.now();
                    Vue._.forEach(responseArray, (response) => {
                        if (response && response.data) {
                            let mappedEvents = Vue._.map(response.data.eventlist, (e) => {
                                let event = methods.mapEvent(e);
                                if (event) {
                                    event.received_at = receivedAt;
                                }
                                return event;
                            });
                            events = events.concat(mappedEvents);
                        } else {
                            console.warn('Wrong data response in getEvents');
                        }
                    });

                    commit('SET_EVENTS', events);
                    resolve(events);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    commit('SET_EVENT_LOADING', false);
                });
        });
    },

    checkinEventById({ commit }, data) {
        let id = Vue._.isObject(data) ? data.id : data;
        let silent = Vue._.isObject(data) && data.silent;
        commit('SET_SINGLE_EVENT_LOADING', !silent);
        commit('SET_SINGLE_EVENT_NATIVE_LOADING', !silent);
        return new Promise((resolve, reject) => {
            api
                .checkinEventById(id)
                .then((response) => {
                    if (response && response.data) {
                        let event = methods.mapEvent(response.data);
                        if (event) {
                            // server clock like in getEvents: current_time plus the time since the response
                            event.received_at = Date.now();
                            // definitions must be in the store before the spot list is
                            // ingested (getSpotsByEventId runs after the checkin resolves)
                            commit('SET_CUSTOM_KEYWORDS', methods.ingestCustomKeywords(event));
                            commit('SET_SELECTED_EVENT', event);
                            resolve(response.data);
                        } else {
                            reject(null);
                        }
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    commit('SET_SINGLE_EVENT_LOADING', false);
                    commit('SET_SINGLE_EVENT_NATIVE_LOADING', false);
                });
        });
    },
    readNewEvent({ commit }, qrData) {
        commit('SET_SINGLE_EVENT_NATIVE_LOADING', true);
        try {
            return new Promise((resolve, reject) => {
                api
                    .readNewEvent({ qr: qrData })
                    .then((response) => {
                        let eventId =
                            response && response.data && response.data.id_event ? response.data.id_event : null;
                        resolve(eventId);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        commit('SET_SINGLE_EVENT_NATIVE_LOADING', false);
                    });
            });
        } catch (e) {
            // Anweisungen für jeden Fehler
            console.log(e); // Fehler-Objekt an die Error-Funktion geben
        }
    },
    readNewPin({ commit }, pinData) {
        commit('SET_SINGLE_EVENT_NATIVE_LOADING', true);
        try {
            return new Promise((resolve, reject) => {
                api
                    .readNewPin(pinData)
                    .then((response) => {
                        let qrData = response && response.data && response.data.qr ? response.data.qr : null;
                        resolve(qrData);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        commit('SET_SINGLE_EVENT_NATIVE_LOADING', false);
                    });
            });
        } catch (e) {
            // Anweisungen für jeden Fehler
            console.log(e); // Fehler-Objekt an die Error-Funktion geben
        }
    },

    getPlayerinfo({ commit, state }) {
        return new Promise((resolve, reject) => {
            api
                .getPlayerinfo({
                    eventId: state.selected_event_id,
                    compact: true,
                })
                .then((response) => {
                    if (response && response.data) {
                        commit('SET_APP_USER_STATISTIC', response.data);
                        commit('SET_APP_TEAM_NAME', response.data.name);
                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },

    getTeamPhoto({ commit, state }) {
        return new Promise((resolve, reject) => {
            api
                .getTeamPhoto({
                    eventId: state.selected_event_id,
                })
                .then((response) => {
                    if (response && response.data) {
                        commit('SET_APP_TEAM_PHOTO', response.data);
                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },
    getTeamInfo({ commit, state }) {
        return new Promise((resolve, reject) => {
            api
                .getTeamInfo({
                    eventId: state.selected_event_id,
                })
                .then((response) => {
                    if (response && response.data) {
                        commit('SET_APP_TEAM', response.data);
                        commit('SET_APP_TEAM_NAME', response.data.team.name);
                        //TODO: is also possible to set person_id and team foto here
                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },

    setTeamName({ commit, state }, data) {
        commit('SET_APP_TEAM_NAME', data);

        return new Promise((resolve, reject) => {
            api
                .setTeamName({
                    teamname: data,
                    eventId: state.selected_event_id,
                })
                .then((response) => {
                    if (response && response.data) {
                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },
    setTeamLeader({ commit, state }, data) {
        return new Promise((resolve, reject) => {
            if (Vue._.isEmpty(data)) {
                reject('empty data object');
            }

            api
                .setTeamLeader({
                    leader_id: data.person_id,
                    eventId: state.selected_event_id,
                })
                .then((response) => {
                    if (response && response.data) {
                        //Set new leader id to local store
                        if (state.app_team && state.app_team.teamleader) {
                            state.app_team.teamleader.id = response.data.leader_id;
                            commit('SET_APP_TEAM', state.app_team);
                        }

                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },
    setParticipantNames({ state }, data) {
        return new Promise((resolve, reject) => {
            let getParams = (data) => {
                let params = {
                    nameCount: 0,
                    name: '',
                };
                data = !Vue._.isArray(data) ? [data] : data;
                if (!Vue._.isEmpty(data)) {
                    params.nameCount = data.length;

                    for (const [i, v] of data.entries()) {
                        let name = i == 0 ? `name` : `name${i}`;
                        params[name] = v;
                    }
                }
                return params;
            };

            let participants = getParams(data);
            participants.eventId = state.selected_event_id;

            api
                .setParticipantNames(participants)
                .then((response) => {
                    if (response && response.data) {
                        resolve(response.data);
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {});
        });
    },

    refresh({ state, dispatch }, data) {
        if (state.selected_event_id) {
            let silent = Vue._.isObject(data) ? data.silent : true;
            dispatch('checkinEventById', { id: state.selected_event_id, silent: silent })
                .then(() => {
                    // a reachable server releases auto-checkin spots parked after a failed solve
                    dispatch('spots/releaseAutoCheckin', null, { root: true });
                })
                .catch((ex) => {
                    console.log(ex);
                });
            //  this.setThemeColorsOnRefresh(state.selected_event_id);
        }
        //Check for loggedIn State
        dispatch('settings/getPlayer', {}, { root: true }).catch((ex) => {
            console.log(ex);
        });
    },

    //Requests event by id. Caution: Cache enabled
    getEventById({ commit, state }, data) {
        let id = Vue._.isObject(data) ? data.id : data;
        let silent = Vue._.isObject(data) && data.silent;

        commit('SET_SINGLE_EVENT_LOADING', !silent);
        commit('SET_SINGLE_EVENT_NATIVE_LOADING', !silent);
        return new Promise((resolve, reject) => {
            if (null == id) {
                reject(null);
                return;
            }
            //Search in cached files
            let item = Utils.getBy('event_id', state.events, id);
            if (!Vue._.isEmpty(item)) {
                resolve(item);
                return;
            }
            //Request server
            api
                .getEventById(id)
                .then((response) => {
                    if (response && response.data) {
                        resolve(methods.mapEvent(response.data));
                    } else {
                        reject(null);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        }).finally(() => {
            commit('SET_SINGLE_EVENT_LOADING', false);
            commit('SET_SINGLE_EVENT_NATIVE_LOADING', false);
        });
    },

    destroyEvent({ commit }) {
        commit('SET_SELECTED_EVENT', {});
        commit('SET_CUSTOM_KEYWORDS', {});
        commit('SET_APP_USER_STATISTIC', null);
        commit('SET_APP_TEAM_PHOTO', null);
        commit('SET_GAMETYPE', CONFIG.GAMETYPE_LBG);
        commit('SET_APP_IDENTIFIER', null);
    },

    destroy({ commit }) {
        commit('SET_EVENTS', []);
        commit('SET_SELECTED_EVENT', {});
        commit('SET_CUSTOM_KEYWORDS', {});
        commit('SET_APP_USER_STATISTIC', null);
        commit('SET_APP_TEAM_PHOTO', null);
        commit('SET_GAMETYPE', CONFIG.GAMETYPE_LBG);
        commit('SET_APP_IDENTIFIER', null);
    },
    destroyLocalStoredGameData({ commit }) {
        commit('SET_ENTERED_PASSWORDS', []);
    },
};

// mutations
const mutations = {
    SET_EVENTS(state, events) {
        //TODO MERGE WENN PAGING
        let newEntries = Vue._.isArray(events) ? events : [];
        Vue.set(state, 'events', newEntries);
    },
    SET_SELECTED_EVENT(state, event) {
        let eventId = event ? event.id_event : null;
        let gametype_id = event ? event.gametype_id : CONFIG.GAMETYPE_LBG;
        let app_id = event ? event.app_id : null;
        Vue.set(state, 'selected_event', event);
        Vue.set(state, 'selected_event_id', eventId);
        Vue.set(state, 'event_timezone', event.timezone);
        Vue.set(state, 'gametype', gametype_id);
        Vue.set(state, 'app_identifier', app_id);
    },
    // N-th task checkout derived locally after a solve; ignored without an event or for another event
    SET_FIRST_TASK_CHECKOUT_TIME(state, { eventId, value }) {
        if (Vue._.isEmpty(state.selected_event) || String(eventId) !== String(state.selected_event_id)) {
            return;
        }
        Vue.set(state.selected_event, 'first_task_checkout_time', value);
    },

    ADD_EVENT(state, event) {
        if (!Vue._.isEmpty(event)) {
            let index = Utils.getIndexById(state.events, event.id_event);
            if (index > -1) {
                Vue.set(state.events, index, event);
            } else {
                state.events.push(event);
            }
        }
    },
    DELETE_LOCATION(state, event) {
        if (!Vue._.isEmpty(event)) {
            let index = Utils.getIndexById(state.events, event.id_event);
            if (index > -1) {
                Vue.delete(state.events, index);
            }
        }
    },
    UPDATE_LOCATION(state, updateData) {
        if (!Vue._.isEmpty(updateData) && updateData.event) {
            let index = Utils.getIndexById(state.events, updateData.event.id_event);
            if (index > -1) {
                updateData.data = updateData.data ? updateData.data : {};
                //Merge and override array
                Vue.set(
                    state.events,
                    index,
                    Vue._.mergeWith(updateData.event, updateData.data, (objValue, srcValue) => {
                        if (Vue._.isArray(srcValue)) {
                            return (objValue = srcValue);
                        }
                    }),
                );
            }
        }
    },
    SET_EVENT_LOADING(state, val) {
        Vue.set(state, 'event_loading', val);
    },
    SET_SINGLE_EVENT_LOADING(state, val) {
        Vue.set(state, 'event_single_loading', val);
    },
    SET_SINGLE_EVENT_NATIVE_LOADING(state, val) {
        Vue.set(state, 'event_single_native_loading', val);
    },
    SET_EVENT_ERROR(state, val) {
        Vue.set(state, 'event_error', val);
    },
    SET_API_LOADING(state, val) {
        Vue.set(state, 'api_loading', val);
    },
    SET_APP_USER_STATISTIC(state, stat) {
        Vue.set(state, 'app_user_statistic', stat);
    },
    SET_APP_TEAM_PHOTO(state, stat) {
        Vue.set(state, 'app_team_photo', stat);
    },
    SET_APP_TEAM_NAME(state, stat) {
        Vue.set(state, 'app_team_name', stat);
    },
    SET_APP_TEAM(state, stat) {
        Vue.set(state, 'app_team', stat);
    },
    SET_GAMETYPE(state, stat) {
        Vue.set(state, 'gametype', stat);
    },
    SET_APP_IDENTIFIER(state, stat) {
        Vue.set(state, 'app_identifier', stat);
    },
    SET_ENTERED_PASSWORDS(state, stat) {
        Vue.set(state, 'entered_passwords', stat);
    },
    SET_CUSTOM_KEYWORDS(state, definitions) {
        Vue.set(state, 'custom_keywords', definitions || {});
    },
    ADD_ENTERED_PASSWORD(state, password) {
        if (!Vue._.isEmpty(password)) {
            if (!Vue._.includes(state.entered_passwords, password)) {
                state.entered_passwords.push(password);
            }
        }
    },

    SET_EVENT_SORT_ORDER(state, sort_order) {
        state.event_sort_order = sort_order;
    },

    SET_EVENT_SORT_OPTION(state, sort_option) {
        state.event_sort_option = sort_option;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
